import React from "react";
import { useState, useEffect } from "react";


const stages = [
  {
    label: "Cold",
    color: "#94a3b8",
    accent: "rgba(148,163,184,0.15)",
    glow: "rgba(148,163,184,0.18)",
    tagBg: "#f1f5f9",
    tagColor: "#64748b",
    detail: "First touchpoint via LinkedIn",
    metric: "Day 1",
  },
  {
    label: "Interested",
    color: "#f59e0b",
    accent: "rgba(245,158,11,0.12)",
    glow: "rgba(245,158,11,0.22)",
    tagBg: "#fef3c7",
    tagColor: "#d97706",
    detail: "Opened proposal email 3×",
    metric: "Day 4",
  },
  {
    label: "Contacted",
    color: "#3b82f6",
    accent: "rgba(59,130,246,0.12)",
    glow: "rgba(59,130,246,0.22)",
    tagBg: "#dbeafe",
    tagColor: "#2563eb",
    detail: "Discovery call completed",
    metric: "Day 9",
  },
  {
    label: "Closed Deal",
    color: "#22c55e",
    accent: "rgba(34,197,94,0.12)",
    glow: "rgba(34,197,94,0.28)",
    tagBg: "#dcfce7",
    tagColor: "#16a34a",
    detail: "Contract signed · $24,000",
    metric: "Day 14",
  },
];

const N = stages.length;

const POS = {
  front: { transform: "translateY(0px) scale(1)",       opacity: 1,    zIndex: 3 },
  mid:   { transform: "translateY(24px) scale(0.935)",   opacity: 0.58, zIndex: 2 },
  back:  { transform: "translateY(44px) scale(0.875)",   opacity: 0.28, zIndex: 1 },
  exit:  { transform: "translateY(-110px) scale(0.94)",  opacity: 0,    zIndex: 4 },
};

function posName(offset, animating) {
  if (animating) {
    if (offset === 0) return "exit";
    if (offset === 1) return "front";
    if (offset === 2) return "mid";
    if (offset === 3) return "back";
  } else {
    if (offset === 0) return "front";
    if (offset === 1) return "mid";
    if (offset === 2) return "back";
  }
  return "back";
}

function PipelineCard({ stage, position }) {
  const isActive = position === "front";
  const style = POS[position];

  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        ...style,
        transition: "transform 0.68s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.68s ease",
        transformOrigin: "bottom center",
        willChange: "transform, opacity",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 22,
          padding: "20px 22px 18px",
          boxShadow: isActive
            ? `0 16px 48px ${stage.glow}, 0 2px 8px rgba(0,0,0,0.04)`
            : "0 2px 16px rgba(0,0,0,0.05)",
          border: `1.5px solid ${isActive ? stage.color + "2e" : "#eff2f7"}`,
          transition: "box-shadow 0.6s ease, border-color 0.6s ease",
        }}
      >
        {/* Top row */}
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div
            style={{
              width: 42, height: 42, borderRadius: 13, flexShrink: 0,
              background: isActive ? stage.accent : "#f1f5f9",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700, fontSize: 13.5,
              color: isActive ? stage.color : "#94a3b8",
              transition: "all 0.55s ease",
              letterSpacing: -0.3,
            }}
          >
            SM
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600, fontSize: 14.5,
              color: "#0f172a", letterSpacing: -0.35,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              Sarah Mitchell
            </div>
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12, color: "#94a3b8", marginTop: 1.5,
            }}>
              VP of Product · Arcflow Inc.
            </div>
          </div>

          <div style={{
            background: isActive ? stage.tagBg : "#f8fafc",
            color: isActive ? stage.tagColor : "#94a3b8",
            fontSize: 11, fontWeight: 600,
            fontFamily: "'DM Sans', sans-serif",
            padding: "5px 11px", borderRadius: 99,
            border: `1px solid ${isActive ? stage.color + "22" : "#eef2f7"}`,
            transition: "all 0.55s ease",
            letterSpacing: 0.25, whiteSpace: "nowrap", flexShrink: 0,
          }}>
            {stage.label}
          </div>
        </div>

        {/* Detail row */}
        <div style={{
          marginTop: 14, display: "flex",
          alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
              background: isActive ? stage.color : "#d1d5db",
              boxShadow: isActive ? `0 0 0 3.5px ${stage.accent}` : "none",
              transition: "all 0.55s ease",
            }} />
            <span style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12.5, color: "#64748b",
            }}>
              {stage.detail}
            </span>
          </div>
          <span style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11.5, color: "#b0bac9", fontWeight: 500,
          }}>
            {stage.metric}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 4, marginTop: 14 }}>
          {stages.map((s, i) => {
            const stagePos = stages.indexOf(stage);
            return (
              <div key={s.label} style={{
                flex: 1, height: 3, borderRadius: 99,
                background: i <= stagePos ? s.color : "#e8edf4",
                transition: "background 0.45s ease",
              }} />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function PipelineAnimation() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrent(c => (c + 1) % N);
        setAnimating(false);
      }, 700);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const cards = [
    { stageIdx: current % N,           offset: 0 },
    { stageIdx: (current + 1) % N,     offset: 1 },
    { stageIdx: (current + 2) % N,     offset: 2 },
    ...(animating ? [{ stageIdx: (current + 3) % N, offset: 3 }] : []),
  ];

  return (
      <div style={{
        width: "100%", height: "calc(100% - 120px)", minHeight: 300,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: "#eef2f7",
        padding: "32px 24px",
        paddingBottom:"150px"
      }}>

        <div style={{
          position: "relative",
          width: "100%",
          height: 155,
        }}>
          {cards.map(({ stageIdx, offset }) => (
            <PipelineCard
              key={stageIdx}
              stage={stages[stageIdx]}
              position={posName(offset, animating)}
            />
          ))}
        </div>
      </div>
  );
}